import { Skeleton } from "antd5/lib";
import { Flex, Text } from "styles/utility-components";

import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { FormValues, SavedViewForm } from "components/views/SavedViewForm";
import { useSavedViewAlert } from "lib/hooks/api/lead_subscriptions/useSavedViewAlert";
import { useUpdateSavedView } from "lib/hooks/api/saved_views/useUpdateSavedView";
import { LeadSubscriptionFrequency } from "lib/types/graphQLEnums";
import { ModalView } from "./utils";

type EditViewModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  selectedView: ModalView | null;
};

export function EditViewModal({ isOpen, onOpenChange, selectedView }: EditViewModalProps) {
  const { alert: personalSavedViewAlert, isLoading: isLoadingSubscriptionContents } =
    useSavedViewAlert(selectedView?.id);

  const { mutate: updateSavedView, isLoading: isLoadingUpdateSavedView } = useUpdateSavedView(
    "Edit view modal",
    {
      onSuccess: async (_data, vars) => {
        onOpenChange(false);
        toast.success(`${vars.request.name} updated successfully!`);
      },
      onError: async () => {
        toast.error("Failed to update search");
      },
    },
  );

  const handleSubmit = async (data: FormValues): Promise<void> => {
    if (!selectedView) return;
    updateSavedView({
      request: {
        id: selectedView.id,
        name: data.viewName,
        description: data.viewDescription ?? "",
        alertId: personalSavedViewAlert?.id,
        alertFrequency:
          // "NEVER" is a UI only value, so we need to convert it to undefined for the API
          data.alertFrequency === "NEVER"
            ? undefined
            : (data.alertFrequency as LeadSubscriptionFrequency),
      },
    });
  };

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} padding={24}>
      {isLoadingSubscriptionContents || !selectedView ? (
        <Skeleton style={{ width: 570, height: 340 }} paragraph={{ rows: 8 }} />
      ) : (
        <>
          <Flex column gap={8}>
            <Text h3>Save "{selectedView.name}"</Text>
            <Text>
              Quickly access your most commonly used filters at any time by creating filtered
              searches.
            </Text>
          </Flex>
          <SavedViewForm
            defaultValues={{
              viewName: selectedView.name,
              viewDescription: selectedView.description ?? "",
              alertFrequency: personalSavedViewAlert?.id
                ? personalSavedViewAlert.frequency
                : "NEVER",
            }}
            isStandardView={selectedView.isStandardView}
            onCancel={() => onOpenChange(false)}
            onSubmit={handleSubmit}
            isLoading={isLoadingUpdateSavedView}
          />
        </>
      )}
    </Modal>
  );
}
